import type { ReauthKind } from '@felix/domain';
import { Button, Modal, Select, Textarea } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { EmptyState } from '../../EmptyState.js';
import { StatusBadge } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import type { PersonnelVessel } from '../personnel/types.js';
import { usePmsErrorText } from './EquipmentDrawer.js';
import classes from './PmsWork.module.css';
import type { PmsSource } from './source.js';
import { pmsStatusTone } from './status-tone.js';

/**
 * PMS iş ekranlarının ortak parçaları (plan M7 Task 14a–14d): durum ve öncelik rozetleri, adres
 * sorgusu, gemi seçimi (web; `?vessel=`), hata durumu ve parola penceresi.
 */

/** Durum rozeti: hap, ton `pmsStatusTone()` (A17), metin `pms.work.status.<key>`. */
export function PmsBadge({ status, children }: { status: string; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={pmsStatusTone(status)}>
      {children ?? t(`pms.work.status.${status}`, { defaultValue: status })}
    </StatusBadge>
  );
}

export function PmsPriorityBadge({ priority }: { priority: string }) {
  const { t } = useTranslation();
  return (
    <StatusBadge tone={pmsStatusTone(priority)}>
      {t(`pms.work.priority.${priority}`, { defaultValue: priority })}
    </StatusBadge>
  );
}

/**
 * Adres sorgusu (seçimler adreste kalır; yenilemede aynı ekran açılır). Kabuk yoksa (test) yerel.
 */
export function usePmsQuery(): [URLSearchParams, (patch: Record<string, string | null>) => void] {
  const shell = useShell();
  const [local, setLocal] = useState('');
  const search = shell ? shell.search : local;
  const params = useMemo(() => new URLSearchParams(search ?? ''), [search]);
  // Ardışık iki güncelleme (ör. gemi seçimi, hemen ardından sekme) yeniden çizimi beklemeden
  // birbirinin üstüne kurulur: son yazılan sorgu tutulur, dışarıdan gelen gezinme onu yeniler
  const rendered = useRef(search);
  const latest = useRef(search);
  if (rendered.current !== search) {
    rendered.current = search;
    latest.current = search;
  }
  const update = useCallback(
    (patch: Record<string, string | null>) => {
      const next = new URLSearchParams(latest.current ?? '');
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      const text = next.toString();
      const query = text ? `?${text}` : '';
      if (query === (latest.current || '')) return;
      latest.current = query;
      if (shell) shell.navigate(`${shell.path}${query}`);
      else setLocal(query);
    },
    [shell],
  );
  return [params, update];
}

export interface VesselChoice {
  vessels: PersonnelVessel[] | null;
  vesselId: string | null;
  vessel: PersonnelVessel | null;
  failed: string | null;
  retry: () => void;
}

/** Gemiler ve seçili gemi: web'de `?vessel=`, gemide tek gemi (Task 6b kalıbı). */
export function useVesselChoice(source: PmsSource, requested: string | null): VesselChoice {
  const [vessels, setVessels] = useState<PersonnelVessel[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` yeniden okutur
  useEffect(() => {
    let live = true;
    setFailed(null);
    source
      .vessels()
      .then((list) => {
        if (live) setVessels(list);
      })
      .catch((error: unknown) => {
        if (live) setFailed(errorCode(error));
      });
    return () => {
      live = false;
    };
  }, [source, attempt]);
  const vesselId = useMemo(() => {
    if (!vessels || vessels.length === 0) return null;
    if (source.scope === 'web' && requested && vessels.some((v) => v.id === requested)) {
      return requested;
    }
    return vessels[0]?.id ?? null;
  }, [vessels, requested, source.scope]);
  return {
    vessels,
    vesselId,
    vessel: vessels?.find((v) => v.id === vesselId) ?? null,
    failed,
    retry: () => setAttempt((n) => n + 1),
  };
}

/** Web ofiste gemi seçici (birden çok gemi varsa); gemide çizilmez. */
export function VesselPicker({
  scope,
  vessels,
  value,
  onChange,
}: {
  scope: 'web' | 'ship';
  vessels: readonly PersonnelVessel[] | null;
  value: string | null;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (scope !== 'web' || !vessels || vessels.length < 2) return null;
  return (
    <Select
      aria-label={t('pms.work.vessel')}
      placeholder={t('pms.work.vessel')}
      className={classes.vesselPicker}
      size="sm"
      data={vessels.map((v) => ({ value: v.id, label: v.name }))}
      value={value}
      allowDeselect={false}
      onChange={(id) => {
        if (id) onChange(id);
      }}
    />
  );
}

/** Veri okunamadı: hata metni ve "Retry". */
export function LoadFailed({ code, onRetry }: { code: string; onRetry: () => void }) {
  const { t } = useTranslation();
  const errorText = usePmsErrorText();
  return (
    <EmptyState
      tone="danger"
      title={t('pms.work.loadError')}
      description={errorText({ code })}
      action={
        <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />} onClick={onRetry}>
          {t('pms.equipment.retry')}
        </Button>
      }
    />
  );
}

/** Yükleniyor: satır iskeleti. */
export function RowsSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className={classes.skeleton} aria-hidden>
      {Array.from({ length: rows }, (_, i) => i).map((row) => (
        <span key={row} style={{ width: `${60 + ((row * 17) % 38)}%` }} />
      ))}
    </div>
  );
}

export interface PmsPasswordDialogProps {
  opened: boolean;
  title: string;
  body: ReactNode;
  /** `required`: iade ve iptalde gerekçe; `optional`: onay yorumu; `none`. */
  reason: 'required' | 'optional' | 'none';
  reasonLabel?: string;
  confirmLabel: string;
  /** Tehlikeli eylem (iade, iptal): kırmızı düğme. */
  danger?: boolean;
  onClose: () => void;
  /**
   * Başarılıysa `null`, değilse hata. `password` yalnız girildiyse gelir (ADR 0075); çağıran
   * gövdeye yalnız o zaman koyar.
   */
  onConfirm: (input: { reason: string; password?: string }) => Promise<unknown>;
  /**
   * Onayın kayıt türü (ADR 0075): parola alanı yalnız şirket ayarı açıkken ya da sunucu isterse
   * görünür; kapalıyken bilgi metni. Verilmezse (kritik işlem) parola zorunludur; onay ve imza
   * pencereleri türü mutlaka verir.
   */
  reauthKind?: ReauthKind;
  /**
   * Hata metni (varsayılan PMS metinleri). Pencere kayıt türünden bağımsızdır: ISM form kaydı da
   * kendi metinleriyle kullanır (M8b3; ikinci bir parola penceresi yazılmaz).
   */
  errorText?: (error: unknown) => string;
  /** Gerekçenin en çok uzunluğu (varsayılan 1000; ISM form kaydında 2000, ADR 0042 §13). */
  reasonMaxLength?: number;
  /** Parolasız türde bilgi metni (onay olmayan işlem; `ReauthField.note`). */
  note?: string;
}

/**
 * İmza penceresi (K7): gerekçe ve açık onay adımı (düğme). Oturum yeterlidir (ADR 0075); şirket
 * ayarı "Onayda parolayı her yerde iste" açıkken parola alanı görünür. Pencere ISM formu ve PMS
 * kayıtlarında ortaktır (`reauthKind`).
 */
export function PmsPasswordDialog(props: PmsPasswordDialogProps) {
  const { t } = useTranslation();
  const pmsErrorText = usePmsErrorText();
  const errorText = props.errorText ?? pmsErrorText;
  const [reason, setReason] = useState('');
  const reauth = useReauth(props.reauthKind);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const reset = () => {
    setReason('');
    reauth.reset();
    setError(null);
  };
  const close = () => {
    reset();
    props.onClose();
  };
  const missingReason = props.reason === 'required' && reason.trim() === '';
  return (
    <Modal
      opened={props.opened}
      onClose={close}
      title={props.title}
      closeButtonProps={{ 'aria-label': t('pms.work.password.back') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (missingReason || reauth.missing) return;
          setBusy(true);
          setError(null);
          try {
            const failure = await props.onConfirm({ reason: reason.trim(), ...reauth.body() });
            if (failure) {
              reauth.noteError(failure);
              setError(failure);
            } else reset();
          } catch (caught) {
            reauth.noteError(caught);
            setError(caught);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className={classes.dialogBody}>{props.body}</div>
        {props.reason !== 'none' ? (
          <Textarea
            label={props.reasonLabel ?? t('pms.work.password.reason')}
            required={props.reason === 'required'}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            maxLength={props.reasonMaxLength ?? 1000}
            autosize
            minRows={2}
            mb="sm"
          />
        ) : null}
        <ReauthField
          reauth={reauth}
          label={t('pms.work.password.password')}
          description={t('pms.work.password.hint')}
          {...(props.note ? { note: props.note } : {})}
        />
        {error ? (
          <p className={classes.error} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={close}>
            {t('pms.work.password.back')}
          </Button>
          <Button
            type="submit"
            {...(props.danger ? { color: 'red' } : {})}
            loading={busy}
            disabled={missingReason || reauth.missing}
          >
            {props.confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
